import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Paper,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useAuth } from '../context/AuthContext';
import { navigationConfig } from '../config/navigation';

const STAT_CARDS = [
  { label: 'Events', icon: EventIcon, color: '#635bff', path: '/events', feature: 'EVENT_MANAGEMENT' },
  { label: 'Ticket Operations', icon: ConfirmationNumberIcon, color: '#10b981', path: '/sell', feature: 'PURCHASE_TICKET' },
  { label: 'Users', icon: PeopleIcon, color: '#f59e0b', path: '/user-management', feature: 'USER_MANAGEMENT' },
  { label: 'Reports', icon: AssessmentIcon, color: '#ef4444', path: '/audit', feature: 'AUDIT' },
];

const DashboardHome = () => {
  const navigate = useNavigate();
  const { hasFeature, adminProfile } = useAuth();

  const quickActions = navigationConfig
    .filter((group) => group.children)
    .flatMap((group) => group.children)
    .filter((item) => !item.feature || hasFeature(item.feature))
    .slice(0, 8);

  const ROLE_LABELS = {
    SUPERADMIN: 'Super Admin',
    ADMIN: 'Office Admin',
    TICKET_VOLUNTEER: 'Volunteer',
  };
  const roleLabel = ROLE_LABELS[adminProfile?.role] || 'Admin';

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Overview of your admin portal. Select a section from the sidebar or use quick actions below.
        </Typography>
      </Box>

      {/* Summary cards */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        {STAT_CARDS.filter((c) => !c.feature || hasFeature(c.feature)).map((card) => {
          const Icon = card.icon;
          return (
            <Grid size={{ xs: 6, sm: 3 }} key={card.label}>
              <Paper
                elevation={0}
                onClick={() => navigate(card.path)}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: card.color,
                    boxShadow: `0 4px 12px ${card.color}20`,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: `${card.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1.5,
                  }}
                >
                  <Icon sx={{ color: card.color, fontSize: 24 }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {card.label}
                </Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Quick actions */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          {quickActions.map((item) => {
            const Icon = item.icon;
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={item.id}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: 'grey.200',
                    '&:hover': {
                      borderColor: '#635bff',
                      boxShadow: '0 2px 8px rgba(99, 91, 255, 0.12)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <CardActionArea onClick={() => navigate(item.path)} sx={{ p: 2 }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '0 !important' }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1.5,
                          bgcolor: 'rgba(99, 91, 255, 0.08)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 20, color: '#635bff' }} />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.label}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    </Box>
  );
};

export default DashboardHome;
